import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Check, Circle, ChevronRight, Building2, Wand2, MapPin, Megaphone, X, Sparkles } from "lucide-react";

const ICONS = { producer: Building2, story: Wand2, map: MapPin, game: Megaphone };

const LABELS = {
  producer: {
    fr: { title: "Créer votre fiche domaine", body: "10 min de conversation avec geoVINUM AI pour générer votre profil complet." },
    en: { title: "Create your winery page",    body: "10 min chat with geoVINUM AI to generate your full profile." },
  },
  story: {
    fr: { title: "Publier votre Story",         body: "7 sections éditoriales générées et retouchables à volonté." },
    en: { title: "Publish your Story",          body: "7 editorial sections generated and editable at will." },
  },
  map: {
    fr: { title: "Créer une carte",             body: "Parcours de visite, parcellaire ou affiche 360° imprimable." },
    en: { title: "Build a map",                 body: "Visit route, parcel map or printable 360° poster." },
  },
  game: {
    fr: { title: "Lancer un quiz sponsorisé",   body: "Quiz interactif avec QR code, récompense et capture de leads." },
    en: { title: "Launch a sponsored quiz",     body: "Interactive quiz with QR code, reward and lead capture." },
  },
};

const HEADER = {
  fr: { title: "Bienvenue dans l'Espace Pro", sub: "4 étapes pour tirer parti de geoVINUM Pro.", dismiss: "Masquer", complete: "🎉 Onboarding complet !" },
  en: { title: "Welcome to Pro space",         sub: "4 steps to make the most of geoVINUM Pro.", dismiss: "Hide",     complete: "🎉 Onboarding complete!" },
};

export default function ProOnboardingChecklist() {
  const { lang } = useT();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const DISMISS_KEY = user ? `pro_onboarding_dismissed_${user.id}` : null;

  useEffect(() => {
    if (!user) return;
    if (DISMISS_KEY && localStorage.getItem(DISMISS_KEY) === "1") {
      setDismissed(true);
    }
    api.get("/pro/onboarding-status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [user, DISMISS_KEY]);

  // Hide when: no data, dismissed, or all steps done
  if (!user || !status || dismissed) return null;
  if (status.all_done) return null;

  const meta = lang === "fr" ? HEADER.fr : HEADER.en;
  const pct = Math.round((status.completed / status.total) * 100);

  const onDismiss = () => {
    if (DISMISS_KEY) localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="mb-6 rounded-2xl bg-gradient-to-br from-[#FFF8FB] via-white to-[#FBF7F3] border border-[#E5276A]/20 p-5 md:p-6 relative overflow-hidden"
         data-testid="pro-onboarding-checklist">
      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        data-testid="pro-onboarding-dismiss"
        aria-label={meta.dismiss}
        className="absolute top-3 right-3 w-7 h-7 rounded-full text-[#8B95A1] hover:bg-[#FCE4ED] hover:text-[#E5276A] flex items-center justify-center transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="p-1.5 rounded-lg bg-[#E5276A] text-white shrink-0">
          <Sparkles className="w-4 h-4" />
        </span>
        <div className="pr-8">
          <h2 className="font-display text-xl text-[#1F3A5F] leading-tight">{meta.title}</h2>
          <p className="text-sm text-[#4A5568] mt-0.5">{meta.sub}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-[#1F3A5F]/10 overflow-hidden">
          <div
            data-testid="pro-onboarding-progressbar"
            className="h-full bg-gradient-to-r from-[#E5276A] to-[#F9C548] rounded-full transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-xs font-bold text-[#1F3A5F] tabular-nums shrink-0">
          {status.completed}/{status.total}
        </span>
      </div>

      {/* Steps */}
      <ol className="mt-5 space-y-2">
        {status.steps.map((s, idx) => {
          const label = LABELS[s.key]?.[lang === "fr" ? "fr" : "en"] || { title: s.key, body: "" };
          const Icon = ICONS[s.key] || Circle;
          return (
            <li key={s.key}>
              <Link
                to={s.cta_url}
                data-testid={`pro-onboarding-step-${s.key}`}
                className={`group flex items-center gap-3 rounded-xl border p-3 transition-colors ${
                  s.done
                    ? "border-[#4CAF50]/30 bg-[#F1F8F1] hover:bg-[#E9F4E9]"
                    : "border-[#1F3A5F]/10 bg-white hover:border-[#E5276A]/40 hover:bg-[#FFF8FB]"
                }`}
              >
                <span
                  className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    s.done ? "bg-[#4CAF50] text-white" : "bg-[#FCE4ED] text-[#E5276A]"
                  }`}
                >
                  {s.done ? <Check className="w-4 h-4" strokeWidth={3} /> : <Icon className="w-4 h-4" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1]">
                      {idx + 1}.
                    </span>
                    <span className={`font-display text-sm ${s.done ? "text-[#4A5568] line-through decoration-2 decoration-[#4CAF50]/60" : "text-[#1F3A5F]"}`}>
                      {label.title}
                    </span>
                  </div>
                  {!s.done && (
                    <div className="text-xs text-[#4A5568] mt-0.5">{label.body}</div>
                  )}
                </div>
                {!s.done && (
                  <ChevronRight className="w-4 h-4 text-[#8B95A1] group-hover:text-[#E5276A] group-hover:translate-x-0.5 transition-all shrink-0" />
                )}
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
